import { gsap } from "gsap";
import { isEventKey } from "keycode";
import { getKeyboardFocusableElements } from "../utils";
import { Tabs } from "./tabs";

const CLASSES = {
  animate: "animate",
  hidden: "hidden",
  active: "tab-active",
};

const SELECTORS = {
  tabpanelImage: ".js-image-animate",
  tabpanelQuote: ".js-quote-animate",
  l3Container: ".main-menu__panel__l3-items",
};

class L2Tabs extends Tabs {
  constructor(context, tabSelector) {
    super(context, tabSelector);
    this.tabpanelFocusElements = [];
    this.activeTabpanel = null;
    this.keyboardNavigation = false;
    this.isAnimating = false;
  }

  init() {
    super.init();

    this.tabpanelImage = this.context.querySelector(SELECTORS.tabpanelImage);
    this.tabpanelQuote = this.context.querySelector(SELECTORS.tabpanelQuote);
    this.l3Container = this.context.querySelector(SELECTORS.l3Container);
    this.tabs.forEach((tab, index, tabsArray) =>
      this.setTabpanelFocusElements(tab, index, tabsArray),
    );
    this.tabpanelFocusElementsListeners();
  }

  subscribe() {
    const reducedMotionQuery = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    );

    reducedMotionQuery.addListener(() => {
      this.reducedMotionActive = reducedMotionQuery.matches;
    });
    this.reducedMotionActive = reducedMotionQuery.matches;

    this.tabs.forEach((tab) => {
      tab.addEventListener("click", (event) => {
        const currentTab = event.currentTarget;
        const isNotActiveTab = !currentTab.classList.contains(CLASSES.active);

        if (!isNotActiveTab || this.isAnimating) {
          return;
        }

        event.preventDefault();
        this.isAnimating = true;

        if (isNotActiveTab && !this.activeTabpanel) {
          this.animateImageQuote(currentTab);
        } else if (isNotActiveTab) {
          this.toggleTab(currentTab);
        }
      });

      tab.addEventListener("mouseover", (event) => {
        const currentTab = event.currentTarget;
        const isNotActiveTab = !currentTab.classList.contains(CLASSES.active);

        if (!isNotActiveTab || this.isAnimating) {
          return;
        }

        event.preventDefault();
        this.isAnimating = true;

        if (isNotActiveTab && !this.activeTabpanel) {
          this.animateImageQuote(currentTab);
        } else if (isNotActiveTab) {
          this.toggleTab(currentTab);
        }
      });

      tab.addEventListener("keydown", (event) => {
        const currentTab = event.currentTarget;
        const isEnterKey = isEventKey(event, "enter");
        const isSpaceKey = isEventKey(event, "space");
        const isRightArrow = isEventKey(event, "right");

        if (this.isAnimating) {
          return;
        }

        if (isEnterKey || isSpaceKey || isRightArrow) {
          event.preventDefault();
          this.keyboardNavigation = true;
          this.isAnimating = true;

          if (!this.activeTabpanel) {
            this.animateImageQuote(currentTab);
          } else {
            this.toggleTab(currentTab);
          }
        }
      });
    });
  }

  resetPanel() {
    this.activeTabpanel = null;
    this.isAnimating = false;
  }

  animateImageQuote(tab) {
    if (this.reducedMotionActive) {
      this.toggleTab(tab);
    } else if (this.tabpanelQuote && this.tabpanelQuote) {
      gsap.to([this.tabpanelImage, this.tabpanelQuote], {
        duration: 0.18,
        autoAlpha: 0,
        ease: "Power2.out",
        onComplete: () => {
          this.toggleTab(tab);
          gsap.to([this.tabpanelImage, this.tabpanelQuote], { autoAlpha: 1 });
        },
      });
    } else {
      this.toggleTab(tab);
    }
  }

  toggleTab(tab) {
    this.makeAllTabsInactive().hideTabpanels(tab).makeTabActive(tab);

    this.onToggleCbStack.forEach((fn) => {
      fn(this, tab);
    });

    return this;
  }

  hideTabpanels(tab) {
    if (this.reducedMotionActive && this.activeTabpanel) {
      this.activeTabpanel.classList.add(CLASSES.hidden);
      this.showTabpanel(tab);

      return this;
    }

    if (
      this.activeTabpanel &&
      this.activeTabpanel.dataset.tab !== tab.dataset.tab
    ) {
      this.activeTabpanel.classList.add(CLASSES.hidden);
    }

    this.showTabpanel(tab);

    return this;
  }

  showTabpanel(element) {
    const tabUid = element.dataset.tab;
    const tabpanel = this.getAssociatedTabpanel(tabUid);
    this.l3Container.scrollTop = 0;
    if (this.reducedMotionActive) {
      this.activeTabpanel = tabpanel;

      gsap.set(this.activeTabpanel, { y: 0, autoAlpha: 1 });
      this.activeTabpanel.classList.remove(CLASSES.hidden);
      this.isAnimating = false;

      if (this.keyboardNavigation) {
        this.goToFirstElement(element);
        this.keyboardNavigation = false;
      }

      return this;
    }

    if (!this.activeTabpanel || this.activeTabpanel.dataset.tab !== tabUid) {
      this.activeTabpanel = tabpanel;

      this.activeTabpanel.classList.remove(CLASSES.hidden);

      let onCompleteFn = () => {
        this.keyboardNavigation = false;
        this.isAnimating = false;
      };

      if (this.keyboardNavigation) {
        onCompleteFn = () => {
          this.goToFirstElement(element);
          this.keyboardNavigation = false;
          this.isAnimating = false;
        };
      }

      gsap.from(this.activeTabpanel, {
        duration: 0.18,
        y: "+=100",
        autoAlpha: 0,
        ease: "Power2.out",
        onComplete: onCompleteFn,
      });
    } else if (this.activeTabpanel.dataset.tab === tabUid) {
      this.isAnimating = false;
    }

    if (this.keyboardNavigation) {
      this.goToFirstElement(element);
      this.keyboardNavigation = false;
      this.isAnimating = false;
    }

    return this;
  }

  setTabpanelFocusElements(tab, index, tabsArray) {
    const currentPanel = this.getAssociatedTabpanel(tab.dataset.tab);
    const nextTab = tabsArray[index + 1];

    if (currentPanel) {
      const focusableElements = getKeyboardFocusableElements(
        currentPanel,
        false,
      );
      const firstElement = focusableElements[0];
      const lastElement = focusableElements.pop();

      this.tabpanelFocusElements.push({
        dataset: currentPanel.dataset.tab,
        tab,
        nextTab,
        firstElement,
        lastElement,
      });
    }
  }

  tabpanelFocusElementsListeners() {
    this.tabpanelFocusElements.forEach((tabpanel) => {
      const { firstElement, lastElement, tab, nextTab } = tabpanel;

      if (firstElement) {
        firstElement.addEventListener("keydown", (event) => {
          const shiftTab = event.shiftKey && isEventKey(event, "tab");
          const leftArrow = isEventKey(event, "left");

          if (shiftTab || leftArrow) {
            event.preventDefault();
            tab.focus();
          }
        });
      }

      if (lastElement) {
        lastElement.addEventListener("keydown", (event) => {
          if (nextTab && !event.shiftKey && isEventKey(event, "tab")) {
            event.preventDefault();
            nextTab.focus();
          }
        });
      }
    });
  }

  goToFirstElement(tab) {
    this.tabpanelFocusElements
      .filter((tabpanel) => tabpanel.dataset === tab.dataset.tab)
      .forEach((tabpanel) => {
        const { firstElement } = tabpanel;

        if (firstElement) {
          firstElement.focus();
        }
      });
  }
}

export { L2Tabs };
